import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function BonusSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const bonuses = [{
    id: 1,
    title: 'The $500 Vault Materials Master List',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/4DVj96KionDXbdG5Sjmk2q/bono_1_bunker_.png',
    problem: "The #1 reason people stall before they build? They don't know exactly what to buy — and end up with $200 in wrong materials and missing 3 critical items they actually need.",
    solution: 'This bonus eliminates that problem completely. Inside, you get:',
    bullets: ['The exact specs for every material you need (no brand guessing, no "tactical" markup)', 'A one-cart printable checklist — screenshot it, walk in, walk out', 'A substitution matrix so your build never stalls if a shelf is empty', 'A budget vs. upgrade breakdown — know exactly where to spend and where to save', 'A 10-minute cost calculator with 3 budget tiers ($250 / $350 / $500)'],
    bottomLine: "You'll know exactly what to buy, what to skip, and what to grab if something's out of stock. No wasted trips. No wasted money."
  }, {
    id: 2,
    title: 'The Invisible Placement Playbook',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/7MvoVLSeywt95VxwLFJsHn/boono_2_bunker.png',
    problem: "You can build the most structurally sound vault in the world… and completely ruin it by putting it in the wrong spot. A hidden vault in a predictable location isn't hidden — it's just delayed.",
    solution: 'The Invisible Placement Playbook gives you the stealth logic:',
    bullets: ['The "Thief Search Pattern" — the exact sequence most burglars follow (so you can stay out of it)', 'Top dead-space zones in real American homes that get consistently overlooked', 'A "Do NOT Place Here" red-flag list — so you don\'t accidentally make your vault a target', 'A 10-minute Placement Scorecard — score your locations and pick the winner fast', 'The "Contractor Test" script — validate your spot without revealing your plan to anyone', '3 real placement examples with the reasoning behind each one'],
    bottomLine: 'You stop second-guessing yourself — and don\'t accidentally undo the entire point of "hidden" with a bad location choice.'
  }, {
    id: 3,
    title: 'Fire + Moisture Protection Protocol (Rust-Proof Kit)',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/rEnvRa1TEhLPF82mCHneyu/bono_3_bunker_.png',
    problem: "Most gear doesn't get destroyed during a fire or flood. It gets destroyed after — by humidity, smoke residue, and corrosion that set in quietly while the damage goes unnoticed.",
    solution: 'A $20 setup and a 5-minute monthly habit can protect all of it. The Fire + Moisture Protection Protocol gives you:',
    bullets: ['The "3 Silent Killers" breakdown — humidity, condensation swings, and smoke residue explained simply', 'A 10-minute protection setup that works in any U.S. climate (hot, cold, humid, dry, coastal)', 'A simple desiccant placement map so you set it correctly the first time', 'A 5-minute monthly maintenance checklist (set a reminder, do the check, done)', "A 60-second quick check for when you're busy", 'After-Fire first-hour checklist — step-by-step to prevent secondary corrosion', 'After-Flood first-hour checklist — the #1 mistake is sealing it back up wet. This stops that.'],
    bottomLine: "Your vault doesn't just stay hidden. What's inside it survives time, climate, and real events — nationwide."
  }];
  return <section ref={ref} className="w-full bg-dark-section py-16 md:py-24" data-id="element-20">
      <div className="max-w-5xl mx-auto px-6" data-id="element-21">
        {/* Intro Header */}
        <motion.div className="mb-16 max-w-3xl mx-auto" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6
      }} data-id="element-22">
          {/* Top label */}
          <div className="flex items-center gap-4 mb-8" data-id="element-23">
            <div className="flex-1 h-px bg-white/8" data-id="element-24" />
            <span className="font-inter text-xs font-bold tracking-[0.25em] text-orange-accent/80 uppercase" data-id="element-25">
              Order Today &amp; Get
            </span>
            <div className="flex-1 h-px bg-white/8" data-id="element-26" />
          </div>

          {/* Headline block */}
          <div className="mb-8" data-id="element-27">
            <h2 className="font-bebas text-5xl md:text-6xl lg:text-7xl text-white tracking-wide leading-none mb-2" data-id="element-28">
              3 FREE <span className="text-orange-accent" data-id="element-29">BONUSES</span>
            </h2>
            <p className="font-inter text-sm text-concrete/50 tracking-[0.15em] uppercase" data-id="element-30">
              Valued at $141 — included at no extra cost
            </p>
          </div>

          {/* Body copy */}
          <p className="font-inter text-base md:text-lg text-concrete/80 leading-relaxed mb-8 max-w-2xl" data-id="element-31">
            The Blueprint tells you exactly how to build it.{' '}
            <strong className="text-white" data-id="element-32">
              These 3 bonuses make sure you actually do.
            </strong>{' '}
            No stalling at the hardware store. No second-guessing the location.
            No watching humidity quietly destroy everything you worked to
            protect.
          </p>

          {/* Three friction points — tactical card style */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-33">
            {[{
            num: '01',
            question: 'What do I buy?',
            sub: 'Exact materials list — no guessing'
          }, {
            num: '02',
            question: 'Where do I put it?',
            sub: 'Stealth placement logic'
          }, {
            num: '03',
            question: 'What if something goes wrong?',
            sub: 'Fire & moisture protocol'
          }].map(item => <div key={item.num} className="bg-dark-bg border border-white/8 px-4 py-4 flex flex-col gap-1" data-id="element-34">
                <span className="font-inter text-xs font-bold text-orange-accent/60 tracking-[0.2em]" data-id="element-35">
                  {item.num}
                </span>
                <p className="font-bebas text-lg text-white tracking-wide leading-tight" data-id="element-36">
                  {item.question}
                </p>
                <p className="font-inter text-xs text-concrete/50 leading-snug" data-id="element-37">
                  {item.sub}
                </p>
              </div>)}
          </div>
        </motion.div>

        {/* Bonus Cards */}
        <motion.div className="space-y-12 mb-12" variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} data-id="element-38">
          {bonuses.map(bonus => <motion.div key={bonus.id} variants={itemVariants} className="bg-dark-card border border-orange-accent/20 rounded-sm overflow-hidden flex flex-col md:flex-row" data-id="element-39">
              {/* Image Side */}
              <div className="md:w-1/3 bg-dark-bg/50 p-6 flex items-center justify-center border-b md:border-b-0 md:border-r border-white/5" data-id="element-40">
                <img src={bonus.image} alt={bonus.title} className="w-full max-w-[240px] shadow-2xl" data-id="element-41" />
              </div>

              {/* Content Side */}
              <div className="md:w-2/3 p-6 md:p-10 flex flex-col" data-id="element-42">
                <div className="flex flex-wrap items-baseline gap-3 mb-2" data-id="element-43">
                  <span className="font-bebas text-orange-accent text-xl tracking-wide" data-id="element-44">
                    FREE BONUS #{bonus.id}
                  </span>
                  <span className="font-inter text-concrete/60 text-sm font-medium" data-id="element-45">
                    (Valued at {bonus.value} — Yours FREE today)
                  </span>
                </div>
                <h3 className="font-bebas text-3xl text-white mb-4 tracking-wide leading-none" data-id="element-46">
                  {bonus.title}
                </h3>
                <p className="font-inter text-concrete mb-4" data-id="element-47">
                  <strong className="text-white" data-id="element-48">{bonus.problem}</strong>
                </p>
                <p className="font-inter text-concrete mb-5" data-id="element-49">
                  {bonus.solution}
                </p>

                <ul className="space-y-3 mb-6" data-id="element-50">
                  {bonus.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-51">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-orange-accent/10 flex items-center justify-center" data-id="element-52">
                        <CheckIcon className="w-3 h-3 text-orange-accent" data-id="element-53" />
                      </div>
                      <span className="font-inter text-sm text-concrete/90 leading-relaxed" data-id="element-54">
                        {bullet}
                      </span>
                    </li>)}
                </ul>

                <div className="mt-auto pt-5 border-t border-white/10" data-id="element-55">
                  <p className="font-inter text-sm text-concrete italic" data-id="element-56">
                    <strong className="text-white not-italic" data-id="element-57">
                      Bottom line:
                    </strong>{' '}
                    {bonus.bottomLine}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Urgency Bridge & CTA */}
        <motion.div className="text-center max-w-2xl mx-auto mt-12" initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {
        opacity: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} data-id="element-58">
          <p className="font-inter text-concrete mb-8 leading-relaxed" data-id="element-59">
            These bonuses are included{' '}
            <strong className="text-white" data-id="element-60">
              because most people fail at execution — not because they lack
              motivation.
            </strong>{' '}
            They stall at the hardware store. They pick the wrong spot. They
            build it right… and watch humidity quietly ruin everything inside.{' '}
            <strong className="text-white" data-id="element-61">
              These 3 bonuses close all 3 gaps.
            </strong>
          </p>

          <button onClick={() => document.getElementById('checkout')?.scrollIntoView({
          behavior: 'smooth'
        })} className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-5 bg-orange-accent text-white font-inter font-bold text-base uppercase tracking-wide rounded-sm transition-all duration-300 hover:bg-orange-accent/90 hover:scale-[1.02] shadow-lg shadow-orange-accent/20" data-id="element-62">
            YES, I WANT THE BLUEPRINT + ALL 3 BONUSES
          </button>
        </motion.div>
      </div>
    </section>;
}